import {useEffect, useState} from 'react';
import {BookmarkIcon as BookmarkIconOutline} from '@heroicons/react/24/outline';
import {BookmarkIcon as BookmarkIconSolid} from '@heroicons/react/24/solid';
import {useGetTripsFavoritesQuery, usePostTripsByTripIdFavoriteAndLinkSecretMutation,} from '../../store/backendApi';
import {useAuthenticatedUser} from '../../hooks/useAuthenticatedUser';

interface FavoriteButtonProps {
  tripId: string;
  linkSecret: string | null;
}

export const FavoriteButton = ({ tripId, linkSecret }: FavoriteButtonProps) => {
  const { isLoggedIn } = useAuthenticatedUser();
  const [showLoginNotification, setShowLoginNotification] = useState(false);
  const [isFavorited, setIsFavorited] = useState(false);

  const [toggleFavorite, { isLoading }] = usePostTripsByTripIdFavoriteAndLinkSecretMutation();
  const { data: favoritesData } = useGetTripsFavoritesQuery(undefined, {
    skip: !isLoggedIn,
  });

  // Check if this trip is in the favorites list
  useEffect(() => {
    if (favoritesData && isLoggedIn) {
      const isTripFavorited = favoritesData.some((fav) => fav.trip.id === tripId);
      setIsFavorited(isTripFavorited);
    }
  }, [favoritesData, tripId, isLoggedIn]);

  const handleFavorite = async () => {
    if (!isLoggedIn) {
      setShowLoginNotification(true);
      setTimeout(() => setShowLoginNotification(false), 3000);
      return;
    }

    const result = await toggleFavorite({
      tripId: tripId,
      linkSecret: linkSecret ?? 'none',
    }).unwrap();
    setIsFavorited(result.isFavorited);
  };

  return (
    <div className="relative">
      <button
        onClick={handleFavorite}
        disabled={isLoading}
        className={`
          flex items-center gap-2 px-4 py-2 rounded-xl 
          transition-all duration-200 transform active:scale-95
          ${
            isFavorited
              ? 'bg-action-primary hover:bg-action-primary-hover text-on-brand shadow-lg'
              : 'bg-surface/90 backdrop-blur-sm hover:bg-surface text-main border border-subtle shadow-md'
          }
          ${isLoading ? 'opacity-50 cursor-not-allowed' : 'hover:shadow-xl hover:-translate-y-0.5'}
          focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-400
        `}
        aria-label={isFavorited ? 'Unfollow this trip' : 'Follow this trip'}
      >
        {isFavorited ? (
          <>
            <BookmarkIconSolid className="w-5 h-5" />
            <span className="text-sm ">Following</span>
          </>
        ) : (
          <>
            <BookmarkIconOutline className="w-5 h-5" />
            <span className="text-sm ">Follow</span>
          </>
        )}
      </button>

      {/* Login notification tooltip */}
      {showLoginNotification && (
        <div
          className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 px-4 py-2
          bg-neutral-darkest text-on-dark text-sm rounded-lg shadow-xl whitespace-nowrap z-50
          animate-fade-in"
        >
          Please log in to follow trips
          <div className="absolute -top-1 left-1/2 transform -translate-x-1/2 w-2 h-2 bg-neutral-darkest rotate-45" />
        </div>
      )}
    </div>
  );
};
